import { modelOptions, post, prop, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    SproutLastDownlinkInfoFieldsWithDefaults,
    SproutLastDownlinkInfoSchemaTypes,
} from "../../types/sproutLastDownlinkInfo.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "SproutLastDownlinkInfo" as const;

// Forward declaration to avoid circular dependency
interface SproutInternalStateTracker {
    [key: string]: any;
}

/**
 * Information about the most recent downlink message sent to a Sprout device.
 *
 * This model tracks downlink communications sent from the server to Sprout devices,
 * maintaining state about message delivery and acknowledgment. It serves as a record
 * of the last command or data packet transmitted to the device, allowing the system
 * to monitor communication success and handle failures appropriately.
 *
 * Key features:
 * - Tracks message content (bytes) and delivery metadata
 * - Monitors acknowledgment status via idemp tokens
 * - Manages state updates that should be applied upon successful delivery
 * - Provides failure detection and retry mechanisms
 *
 * The acknowledgment system works by:
 * 1. Server sends downlink with unique idemp token
 * 2. Device processes message and echoes back the idemp token
 * 3. Server matches the echoed token to mark message as received
 * 4. Internal state updates are applied once acknowledgment is confirmed
 */
// TODO: mark as responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SproutLastDownlinkInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutLastDownlinkInfo extends SchemaBase<
    SproutLastDownlinkInfo,
    SproutLastDownlinkInfoFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * The bytes that were sent to the device as part of this downlink, represented as a hex string
     */
    @validate({ zodValidator: z.string(), responseOnly: true })
    @prop({ type: () => String, required: true })
    bytes!: string;

    /**
     * The fPort the downlink was sent to
     */
    @validate({ zodValidator: z.number(), responseOnly: true })
    @prop({ type: () => Number, required: true })
    fPort!: number;

    /**
     * The idemp token associated with the downlink that was sent. When listening for an acknowledgement,
     * we will listen for the idemp token to be echoed.
     */
    @validate({ zodValidator: z.number(), responseOnly: true })
    @prop({ type: () => Number, required: true })
    idemp!: number;

    /**
     * The date and time the downlink was originally sent
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    date?: Date;

    /**
     * When set to true, it indicates this downlink has been received, the internal state has been updated,
     * and we are no longer listening for an acknowledgement.
     */
    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    flaggedAsReceived: boolean = false;

    /**
     * When set to true, it indicates this downlink has not been received. It indicates we are now confident it has
     * failed and we are no longer listening for an acknowledgement.
     */
    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    flaggedAsFailed: boolean = false;

    /**
     * The ID of the log object which has the details of why this downlink was sent. And which will be
     * retroactively updated with information on whether this downlink was successful.
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    interactionID?: string;

    /**
     * Only used in Sprout. The updates that should be sent to the internal state tracker when this downlink
     * is acknowledged.
     *
     * Both array and record types stay allowed because Gen4 used to write an array here, and that data
     * is still in the `devices` collection.
     */
    @validate({
        zodValidator: z.union([z.record(z.string(), z.any()), z.array(z.any())]).optional(),
        responseOnly: true,
    })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    stateUpdates?: Partial<SproutInternalStateTracker> | unknown[];

    static getters = {} as const satisfies Record<string, (self: SproutLastDownlinkInfo) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutLastDownlinkInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SproutLastDownlinkInfo, SproutLastDownlinkInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutLastDownlinkInfo.dtoValidators = getZodSchemaFromClass(
    SproutLastDownlinkInfo,
) as unknown as DtoValidatorTypes<SproutLastDownlinkInfoSchemaTypes>;
